import { createHmac, timingSafeEqual } from "node:crypto";

/**
 * Verify a Meta webhook delivery (X-Hub-Signature-256 over the RAW body).
 *
 * This is the front door of the ingestion path. Everything downstream, the
 * queue, the agent, the draft, the ledger, treats what arrives here as a real
 * customer speaking. An attacker who gets past this can make the shop's agent
 * say things to nobody, or worse, poison a contact's memory.
 *
 * Fails closed on every path, including a missing secret. The secret is passed
 * in by the caller, read from META_APP_SECRET. Only the variable NAME appears
 * in this repo.
 */

export const SIGNATURE_HEADER = "x-hub-signature-256";

export type WebhookFailure =
  | "missing_signature"
  | "no_secret_configured"
  | "unsupported_algorithm"
  | "bad_signature";

export type WebhookVerification =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: WebhookFailure };

/**
 * The `X-Hub-Signature-256` value for `rawBody` under `appSecret`: the inverse
 * of the verifier below, same construction (HMAC-SHA256 over the exact body
 * bytes, `sha256=` prefix, hex). TA-1324 T2: the Meta simulator signs every
 * delivery with it. An empty secret throws, because the verifier refuses one
 * and a delivery signed with it would measure nothing but 401s.
 */
export function signWebhookBody(rawBody: string, appSecret: string): string {
  if (appSecret.length === 0) throw new Error("signWebhookBody needs the app secret, and was given an empty one");
  return `sha256=${createHmac("sha256", appSecret).update(rawBody, "utf8").digest("hex")}`;
}

export function verifyWebhookSignature(
  rawBody: string,
  header: string | undefined,
  appSecret: string,
): WebhookVerification {
  if (appSecret.length === 0) return { ok: false, reason: "no_secret_configured" };
  if (!header) return { ok: false, reason: "missing_signature" };

  // Only sha256. Meta's older sha1= header exists in the wild, and accepting
  // it would let an attacker choose the weaker hash.
  if (!header.startsWith("sha256=")) return { ok: false, reason: "unsupported_algorithm" };

  const provided = Buffer.from(header.slice("sha256=".length), "hex");
  // MUST be the raw body. JSON.stringify(JSON.parse(body)) reorders keys and
  // drops whitespace, so a server that parses before verifying rejects every
  // genuine delivery, and only discovers it against real traffic.
  const expected = createHmac("sha256", appSecret).update(rawBody, "utf8").digest();

  // Length first: timingSafeEqual throws on a mismatch, and a throw here is a
  // 500 rather than a clean rejection.
  if (provided.length !== expected.length) return { ok: false, reason: "bad_signature" };
  if (!timingSafeEqual(provided, expected)) return { ok: false, reason: "bad_signature" };

  return { ok: true };
}
